import type { CreateVerwijsbriefInput, Verwijsbrief } from '@kennisbank/shared';
import { ApiError, apiPost } from '../api/client.js';

/**
 * Verwijsbrief (ADR 0017): aanmaken vanuit Sara, Mila of het overzicht, en de
 * tekst per onderdeel klaarzetten om in het HIS of ZorgDomein te plakken
 * (besluit Jos 12-10-2026, zelfde werkwijze als de SOEP). Patiënt, praktijk,
 * verwijzer, journaal en medicatie komen daar uit het HIS zelf.
 */

type Nieuw = Omit<Partial<CreateVerwijsbriefInput>, 'bron'> & {
  bron: CreateVerwijsbriefInput['bron'];
};

export function maakVerwijsbrief(input: Nieuw, opstellerNaam: string): Promise<Verwijsbrief> {
  return apiPost<Verwijsbrief>('/api/v1/verwijsbrieven', { opstellerNaam, ...input });
}

/**
 * Inhoud laat Mila uit het transcript van de eigen Sara-opname schrijven
 * (besluit Jos 12-10-2026). Is het transcript al gewist (verslag verwerkt in
 * het HIS, of ouder dan 72 uur), dan een leeg concept in plaats van een fout.
 */
export async function maakVerwijsbriefUitSara(
  { saraTaskId, ...input }: Nieuw & { saraTaskId: string | null },
  opstellerNaam: string,
): Promise<Verwijsbrief> {
  if (saraTaskId) {
    try {
      return await apiPost<Verwijsbrief>('/api/v1/verwijsbrieven/uit-sara', {
        opstellerNaam,
        ...input,
        saraTaskId,
      });
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 409)) throw err;
    }
  }
  return maakVerwijsbrief(input, opstellerNaam);
}

export interface Onderdeel {
  titel: string;
  tekst: string;
}

// Titels gelijk aan de vakken in ZorgDomein, zodat elke kopieerknop op één vak past.
export const REDEN = 'Reden van verwijzing, vraagstelling';
export const VOORGESCHIEDENIS = 'Psychiatrische voorgeschiedenis';

/** De onderdelen zoals ze in de brief komen; lege onderdelen vallen weg. */
export function briefOnderdelen(b: Verwijsbrief): Onderdeel[] {
  const { klachten, hulpvraag, eerdereStappen } = b.inhoud;
  // Eén alinea zonder kopjes: zo vraagt ZorgDomein het vak "Reden van
  // verwijzing, vraagstelling" (voorbeeld Kimberley, 26-10-2026).
  const reden = [klachten, hulpvraag]
    .map((t) => t.trim())
    .filter(Boolean)
    .join(' ');
  return [
    {
      titel: 'Aan',
      tekst: b.geadresseerde ? `${b.geadresseerde.naam}\n${b.geadresseerde.adres}` : '',
    },
    { titel: REDEN, tekst: reden },
    { titel: VOORGESCHIEDENIS, tekst: eerdereStappen.trim() },
    {
      titel: 'Afsluiting',
      tekst: `Met vriendelijke groet,\n\nOpgesteld door: ${b.opstellerNaam || '…'}, POH-GGZ`,
    },
  ].filter((o) => o.tekst.trim());
}

/** Hele brief als platte tekst, voor wie in één vak plakt. */
export function briefTekst(b: Verwijsbrief): string {
  return briefOnderdelen(b)
    .map((o) => (o.titel === 'Afsluiting' ? o.tekst : `${o.titel}\n${o.tekst}`))
    .join('\n\n');
}
